(() => {
  if (window.layerformClockPersonalization) {
    window.layerformClockPersonalization.refresh();
    return;
  }

  const selector = '[data-clock-name-personalization]';
  const states = new Map();
  let scheduled = false;

  const formFor = (root) => document.getElementById(root.dataset.productFormId);
  const scopeFor = (root) => formFor(root)?.closest('.shopify-section, dialog, product-card');
  const fieldsFor = (root) => ({
    input: root.querySelector('[data-clock-name]'),
    field: root.querySelector('[data-name-field]'),
    status: root.querySelector('[data-clock-name-status]'),
  });

  const update = (root, state) => {
    const form = formFor(root);
    const { input, field, status } = fieldsFor(root);
    if (!form || !input) return { valid: false, pending: false };

    const formVariant = String(form.querySelector('[name="id"]')?.value || '');
    const variant = state.selectedVariant ?? formVariant;
    const sports = new Set((root.dataset.sportsVariantIds || '').split(',').map((id) => id.trim()).filter(Boolean));
    const needsName = root.dataset.requiredMode === 'always' || sports.has(variant);
    const pending = state.pending || (state.selectedVariant != null && state.selectedVariant !== formVariant);
    const valid = !needsName || Boolean(input.value.trim());

    root.hidden = root.dataset.requiredMode === 'sports' && !needsName;
    root.setAttribute('aria-busy', String(pending));
    if (field) {
      field.hidden = !needsName;
      field.dataset.state = !needsName ? 'idle' : valid ? 'valid' : 'invalid';
    }
    input.required = needsName;
    input.disabled = !needsName;
    input.pattern = '.*\\S.*';
    input.setCustomValidity('');
    if (status) status.hidden = !needsName || valid;

    form.closest('product-form-component')?.querySelectorAll('[name="add"],[ref="addToCartButton"]').forEach((button) => {
      const locked = !valid || pending;
      if (locked) {
        if (!button.classList.contains('clock-name-locked')) {
          state.buttonAria.set(button, button.getAttribute('aria-disabled'));
        }
        button.classList.add('clock-name-locked');
        button.setAttribute('aria-disabled', 'true');
      } else if (button.classList.contains('clock-name-locked')) {
        button.classList.remove('clock-name-locked');
        const previous = state.buttonAria.get(button);
        if (button.disabled || previous === 'true') button.setAttribute('aria-disabled', 'true');
        else if (previous != null) button.setAttribute('aria-disabled', previous);
        else button.removeAttribute('aria-disabled');
        state.buttonAria.delete(button);
      }
    });
    return { valid, pending };
  };

  const refresh = () => {
    scheduled = false;
    states.forEach((_, root) => { if (!root.isConnected) states.delete(root); });
    document.querySelectorAll(selector).forEach((root) => {
      if (!states.has(root)) states.set(root, { selectedVariant: null, pending: false, generation: 0, buttonAria: new WeakMap() });
      update(root, states.get(root));
    });
  };

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(refresh);
  };

  const matchingRoots = (target) => {
    if (!(target instanceof Element)) return [];
    const scope = target.closest('.shopify-section, dialog, product-card');
    const picker = target.closest('variant-picker');
    return [...states].filter(([root]) => {
      if (scopeFor(root) !== scope) return false;
      const productId = formFor(root)?.closest('product-form-component')?.dataset.productId;
      return !picker?.dataset.productId || !productId || picker.dataset.productId === productId;
    });
  };

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('variant-picker')) return;
    refresh();
    const option = target instanceof HTMLSelectElement ? target.selectedOptions[0] : target;
    matchingRoots(target).forEach(([root, state]) => {
      if (option?.dataset.variantId) state.selectedVariant = String(option.dataset.variantId);
      update(root, state);
    });
    schedule();
  }, true);

  document.addEventListener('input', (event) => {
    const root = event.target instanceof Element ? event.target.closest(selector) : null;
    if (root && states.has(root)) update(root, states.get(root));
  });

  const guard = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = event.type === 'click' ? target?.closest('[name="add"],[ref="addToCartButton"]') : null;
    const form = event.type === 'submit' ? target : button?.form;
    if (!(form instanceof HTMLFormElement)) return;
    refresh();
    for (const [root, state] of states) {
      if (formFor(root) !== form) continue;
      const result = update(root, state);
      if (result.valid && !result.pending) continue;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!result.valid) {
        const { input } = fieldsFor(root);
        input?.reportValidity();
        input?.focus({ preventScroll: true });
        root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      break;
    }
  };
  document.addEventListener('click', guard, true);
  document.addEventListener('submit', guard, true);

  import('@shopify/events').then(({ StandardEvents }) => {
    document.addEventListener(StandardEvents.productSelect, (event) => {
      refresh();
      matchingRoots(event.target).forEach(([root, state]) => {
        const generation = ++state.generation;
        const option = event.target.selectedOption;
        const selectedId = event.detail?.variantId || option?.dataset.variantId;
        if (selectedId) state.selectedVariant = String(selectedId);
        state.pending = true;
        update(root, state);
        Promise.resolve(event.promise).then((result) => {
          if (state.generation !== generation || !root.isConnected) return;
          const variant = result?.detail?.resource?.id;
          if (variant != null) state.selectedVariant = String(variant);
          state.pending = false;
          schedule();
        }).catch(() => {
          if (state.generation !== generation) return;
          state.pending = false;
          schedule();
        });
      });
    });
  }).catch(() => {});

  document.addEventListener('shopify:section:load', schedule);
  window.layerformClockPersonalization = { refresh };
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['value', 'data-sports-variant-ids', 'data-product-form-id'],
  });
  refresh();
})();
